import * as React from "react";
import { ArrowRight, Moon, Sparkles, Sun } from "lucide-react";
import { useNavigate } from "react-router";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command";
import { shellRoutes } from "./nav";
import { useTheme } from "../theme-provider";

interface CommandPaletteProps {
  onClose: () => void;
  onOpenZot: () => void;
}

export function CommandPalette({ onClose, onOpenZot }: CommandPaletteProps) {
  const [query, setQuery] = React.useState("");
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const pages = [
    ...new Map(
      shellRoutes.filter((route) => route.path !== "/zot").map((route) => [route.title, route]),
    ).values(),
  ];
  const commands = [
    ...pages.map((page) => ({
      label: page.title,
      detail: page.summary,
      keywords: `${page.title} ${page.path} ${page.summary}`,
      icon: ArrowRight,
      shortcut: page.path === "/runs" ? "G R" : undefined,
      run: () => navigate(page.path),
    })),
    {
      label: "New task",
      detail: "Describe the work in Zot; nothing runs until you confirm",
      keywords: "new task agent start zot chat",
      icon: ArrowRight,
      shortcut: "N",
      run: () => navigate("/zot"),
    },
    {
      label: "Toggle theme",
      detail: `Switch to ${theme === "dark" ? "light" : "dark"} theme`,
      keywords: "theme appearance dark light",
      icon: theme === "dark" ? Sun : Moon,
      shortcut: undefined,
      run: () => setTheme(theme === "dark" ? "light" : "dark"),
    },
    {
      label: "Ask Zot",
      detail: "Ask about this project",
      keywords: "assistant zot help",
      icon: Sparkles,
      shortcut: "⌘ J",
      run: onOpenZot,
    },
  ];
  const results = commands.filter((command) =>
    `${command.label} ${command.detail} ${command.keywords}`
      .toLocaleLowerCase()
      .includes(query.trim().toLocaleLowerCase()),
  );

  function focusResult(direction: 1 | -1) {
    const options = [...(document.querySelectorAll<HTMLElement>("[data-command-option]") ?? [])];
    const first = direction === 1 ? options[0] : options.at(-1);
    first?.focus();
  }

  return (
    <Command
      value={query}
      onValueChange={setQuery}
      className="max-h-[min(34rem,calc(100svh-3rem))]"
    >
      <div className="flex items-center gap-2 border-b px-4 text-muted-foreground">
        <CommandInput
          autoFocus
          aria-label="Search pages and actions"
          placeholder="Search pages, runs, and actions…"
          className="border-0"
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              focusResult(1);
            }
            if (event.key === "ArrowUp") {
              event.preventDefault();
              focusResult(-1);
            }
            if (event.key === "Enter" && results[0]) {
              event.preventDefault();
              onClose();
              results[0].run();
            }
          }}
        />
        <kbd className="hidden rounded border bg-muted px-1.5 py-0.5 font-mono text-xs sm:inline">
          ESC
        </kbd>
      </div>
      <CommandList aria-label="Search results" className="max-h-[min(26rem,calc(100svh-8rem))]">
        {results.length === 0 ? (
          <CommandEmpty>No matching pages or actions.</CommandEmpty>
        ) : (
          <CommandGroup heading="Pages and actions">
            {results.map(({ label, detail, keywords, icon: Icon, shortcut, run }) => (
              <CommandItem
                key={label}
                value={`${label} ${detail} ${keywords}`}
                data-command-option
                onClick={() => {
                  onClose();
                  run();
                }}
                className="min-h-11"
              >
                <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{label}</span>
                  {detail && (
                    <span className="block truncate text-xs text-muted-foreground">{detail}</span>
                  )}
                </span>
                {shortcut && <CommandShortcut>{shortcut}</CommandShortcut>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
}
